import {
  useEffect,
  useState
} from "react";

import {
  useNavigate,
  useParams
} from "react-router-dom";

import CategoryForm from "../components/CategoryForm";

import Button from "../../../components/common/Button";
import Loader from "../../../components/common/Loader";
import ErrorMessage from "../../../components/common/ErrorMessage";

import { useCategories } from "../hooks/useCategories";

const EditCategoryPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    fetchCategory,
    updateCategory
  } = useCategories({
    activeOnly: false
  });

  const [category, setCategory] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState("");

  const [serverError, setServerError] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | LOAD CATEGORY
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    let mounted = true;

    const loadCategory = async () => {
      try {
        setLoading(true);
        setError("");

        const result =
          await fetchCategory(id);

        if (mounted) {
          setCategory(result);
        }
      } catch (error) {
        if (mounted) {
          setError(
            error?.response?.data
              ?.message ||
            "Unable to load category."
          );
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (id) {
      loadCategory();
    }

    return () => {
      mounted = false;
    };
  }, [id, fetchCategory]);


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (data) => {
      try {
        setSaving(true);
        setServerError("");

        const updated =
          await updateCategory(
            id,
            data
          );

        setCategory(updated);

        navigate(
          `/categories/${id}`
        );
      } catch (error) {
        setServerError(
          error?.response?.data
            ?.message ||
          "Unable to update category."
        );

        throw error;
      } finally {
        setSaving(false);
      }
    };


  /*
  |--------------------------------------------------------------------------
  | LOADING
  |--------------------------------------------------------------------------
  */

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader />
      </div>
    );
  }


  /*
  |--------------------------------------------------------------------------
  | ERROR
  |--------------------------------------------------------------------------
  */

  if (error) {
    return (
      <div className="max-w-2xl">
        <ErrorMessage
          message={error}
        />

        <div className="mt-4">
          <Button
            variant="secondary"
            onClick={() =>
              navigate("/categories")
            }
          >
            Back to categories
          </Button>
        </div>
      </div>
    );
  }


  if (!category) {
    return null;
  }


  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <button
          type="button"
          onClick={() =>
            navigate("/categories")
          }
          className="mb-3 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          ← Back to categories
        </button>

        <h1 className="text-2xl font-bold text-slate-900">
          Edit category
        </h1>

        <p className="mt-1 text-sm text-slate-500">
          Update {category.name}'s information.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8">
        <CategoryForm
          mode="edit"
          initialData={category}
          onSubmit={handleSubmit}
          onCancel={() =>
            navigate(
              `/categories/${id}`
            )
          }
          loading={saving}
          serverError={serverError}
        />
      </div>
    </div>
  );
};

export default EditCategoryPage;